import { ThemeEvents } from '@theme/events';
import { viewTransition } from '@theme/utilities';

const STORAGE_KEY = 'look-tabs-lashes-active';
const ACTIVE_TAB_KEY = 'look-tabs-active-tab';

class LookTabsComponent extends HTMLElement {
  #isFiltering = false;
  #isClearingStyles = false;
  #lashesActive = false;

  connectedCallback() {
    this.addEventListener('click', this.#onClick);
    document.addEventListener(ThemeEvents.FilterUpdate, this.#onFilterUpdate);

    // Read persisted lashes state.
    const stored = sessionStorage.getItem(STORAGE_KEY);
    this.#lashesActive = stored === 'true';

    // Re-apply active tab class immediately — Horizon's DOM replacement wipes all
    // look-tabs__link--active classes from freshly rendered HTML, causing a one-frame
    // flash where no tab appears selected. Restore it before first paint.
    this.#restoreActiveTab();

    // Apply sub-row visibility immediately via inline style to win the paint race.
    this.#applySubRowVisibility(this.#lashesActive);
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.#onClick);
    document.removeEventListener(ThemeEvents.FilterUpdate, this.#onFilterUpdate);
  }

  #onClick = async (event) => {
    const link = event.target instanceof Element ? event.target.closest('[data-look-tab]') : null;
    if (!(link instanceof HTMLAnchorElement)) return;
    if (link.classList.contains('look-tabs__link--disabled')) return;
    if (this.#isFiltering) return;

    const section = this.closest('.shopify-section');
    const facetsForm = this.#getFacetsForm(section);
    if (!facetsForm) return;

    event.preventDefault();

    if (viewTransition.current) {
      await viewTransition.current;
    }

    const paramName = link.dataset.filterParam;
    const paramValue = link.dataset.filterValue;
    if (!paramName || paramValue === undefined) return;

    const isLashesTab = link.dataset.isLashes === 'true';
    const isPrimaryTab = link.dataset.filterTier === 'primary';
    const wasLashesActive = isLashesTab && link.classList.contains('look-tabs__link--active');
    const isNowActive = !link.classList.contains('look-tabs__link--active');

    // ── IMMEDIATE STATE LOCK ──────────────────────────────────────────────────
    // Persist active tab value to sessionStorage BEFORE anything async/DOM-replacing
    // happens, so connectedCallback on the re-rendered element reads correct values.
    if (isPrimaryTab) {
      if (isNowActive) {
        // Store which primary tab value is now active
        sessionStorage.setItem(ACTIVE_TAB_KEY, JSON.stringify({
          param: paramName,
          value: paramValue,
          tier: 'primary'
        }));
      } else {
        // Tab is being deselected — clear active tab record
        sessionStorage.removeItem(ACTIVE_TAB_KEY);
      }

      if (isLashesTab) {
        this.#setLashesActive(isNowActive);
      } else {
        this.#setLashesActive(false);
      }
    } else if (link.dataset.filterTier === 'secondary') {
      if (isNowActive) {
        sessionStorage.setItem(ACTIVE_TAB_KEY, JSON.stringify({
          param: paramName,
          value: paramValue,
          tier: 'secondary'
        }));
      } else {
        sessionStorage.removeItem(ACTIVE_TAB_KEY);
      }
    }
    // ─────────────────────────────────────────────────────────────────────────

    this.#isFiltering = true;
    this.#setFilteringState(section, true);

    if (wasLashesActive) {
      this.#clearStyleFilters(facetsForm);
    }

    this.#applyFilterToggle(facetsForm, link, paramName, paramValue);

    try {
      if (viewTransition.current) {
        await viewTransition.current;
      }
    } finally {
      this.#isFiltering = false;
      this.#setFilteringState(section, false);
      this.#applySubRowVisibility(this.#lashesActive);
    }
  };

  #onFilterUpdate = () => {
    if (this.#isFiltering) return;

    const section = this.closest('.shopify-section');
    const facetsForm = this.#getFacetsForm(section);

    this.#setFilteringState(section, false);
    this.#isFiltering = false;

    if (!this.#isClearingStyles && facetsForm && !this.#lashesActive && this.#hasActiveStyleFilters(facetsForm)) {
      this.#isClearingStyles = true;
      this.#clearStyleFilters(facetsForm);

      if (typeof facetsForm.updateFilters === 'function') {
        facetsForm.updateFilters();
      }

      this.#isClearingStyles = false;
    }

    this.#applySubRowVisibility(this.#lashesActive);
  };

  /**
   * Restores the active tab's CSS class from sessionStorage immediately on connect,
   * preventing the unstyled flash caused by Horizon's DOM replacement wiping all
   * active classes from the freshly server-rendered HTML.
   */
  // #restoreActiveTab() {
  //   const raw = sessionStorage.getItem(ACTIVE_TAB_KEY);
  //   if (!raw) return;

  //   try {
  //     const { param, value } = JSON.parse(raw);
  //     const normalize = (s) => String(s ?? '').toLowerCase().replace(/[^a-z0-9]+/g, '-');
  //     const tabs = this.querySelectorAll('[data-look-tab]');

  //     tabs.forEach((tab) => {
  //       if (!(tab instanceof HTMLAnchorElement)) return;
  //       const tabParam = tab.dataset.filterParam;
  //       const tabValue = tab.dataset.filterValue;

  //       const matches =
  //         tabParam === param &&
  //         (tabValue?.toLowerCase() === value?.toLowerCase() || normalize(tabValue) === normalize(value));

  //       if (matches) {
  //         tab.classList.add('look-tabs__link--active');
  //         tab.setAttribute('aria-pressed', 'true');
  //       }
  //     });
  //   } catch {
  //     sessionStorage.removeItem(ACTIVE_TAB_KEY);
  //   }
  // }
#restoreActiveTab() {
  const raw = sessionStorage.getItem(ACTIVE_TAB_KEY);
  if (!raw) return;

  try {
    const { param, value } = JSON.parse(raw);
    const normalize = (s) => String(s ?? '').toLowerCase().replace(/[^a-z0-9]+/g, '-');

    // Validate the stored tab against the actual current URL before trusting it.
    const url = new URL(window.location.href);
    const currentValues = url.searchParams.getAll(param).map((v) => v.toLowerCase());
    const isStillActuallyActive = currentValues.some(
      (v) => v === String(value).toLowerCase() || normalize(v) === normalize(value)
    );

    if (!isStillActuallyActive) {
      // Stale entry from a previous click/navigation — drop it instead of applying it.
      sessionStorage.removeItem(ACTIVE_TAB_KEY);
      return;
    }

    const tabs = this.querySelectorAll('[data-look-tab]');
    tabs.forEach((tab) => {
      if (!(tab instanceof HTMLAnchorElement)) return;
      const tabParam = tab.dataset.filterParam;
      const tabValue = tab.dataset.filterValue;

      const matches =
        tabParam === param &&
        (tabValue?.toLowerCase() === value?.toLowerCase() || normalize(tabValue) === normalize(value));

      if (matches) {
        tab.classList.add('look-tabs__link--active');
        tab.setAttribute('aria-pressed', 'true');
      }
    });
  } catch {
    sessionStorage.removeItem(ACTIVE_TAB_KEY);
  }
}
  /**
   * Updates lashes state, persists to sessionStorage, and applies visibility.
   * @param {boolean} value
   */
  #setLashesActive(value) {
    this.#lashesActive = value;
    if (value) {
      sessionStorage.setItem(STORAGE_KEY, 'true');
    } else {
      sessionStorage.removeItem(STORAGE_KEY);
    }
    this.#applySubRowVisibility(value);
  }

  /**
   * Applies visibility using inline style AND CSS class.
   * Inline style wins during the FOUC window before stylesheets repaint.
   * @param {boolean} visible
   */
  #applySubRowVisibility(visible) {
    const subRow = this.querySelector('[data-look-tabs-sub]');
    if (!subRow) return;

    subRow.style.display = visible ? '' : 'none';
    subRow.classList.toggle('look-tabs__row--hidden', !visible);
  }

  #applyFilterToggle(facetsForm, link, paramName, paramValue) {
    const wasActive = link.classList.contains('look-tabs__link--active');
    const toggledViaCheckbox = this.#toggleFilterCheckbox(facetsForm, paramName, paramValue);

    if (toggledViaCheckbox !== null) {
      this.#setTabPressed(link, toggledViaCheckbox);

      if (typeof facetsForm.updateFilters === 'function') {
        facetsForm.updateFilters();
      }

      return toggledViaCheckbox;
    }

    const isNowActive = !wasActive;
    this.#setTabPressed(link, isNowActive);

    const url = isNowActive ? link.dataset.urlAdd : link.dataset.urlRemove;

    if (url && typeof facetsForm.updateFiltersByURL === 'function') {
      facetsForm.updateFiltersByURL(url);
    } else if (typeof facetsForm.updateFilters === 'function') {
      facetsForm.updateFilters();
    }

    return isNowActive;
  }

  #setFilteringState(section, isFiltering) {
    section?.querySelector('results-list')?.classList.toggle('look-tabs-filtering', isFiltering);
    this.classList.toggle('look-tabs--filtering', isFiltering);
  }

  #setTabPressed(link, isActive) {
    link.classList.toggle('look-tabs__link--active', isActive);
    link.setAttribute('aria-pressed', isActive ? 'true' : 'false');
  }

  #toggleFilterCheckbox(facetsForm, paramName, paramValue) {
    if (!facetsForm) return null;

    const normalize = (s) => String(s ?? '').toLowerCase().replace(/[^a-z0-9]+/g, '-');

    const inputs = facetsForm.querySelectorAll(`input[type="checkbox"][name="${CSS.escape(paramName)}"]`);
    const matchingInputs = Array.from(inputs).filter(
      (input) =>
        input instanceof HTMLInputElement &&
        (input.value.toLowerCase() === paramValue.toLowerCase() || normalize(input.value) === normalize(paramValue))
    );

    if (!matchingInputs.length) return null;

    const currentlyActive = matchingInputs.some((input) => input.checked);
    const isActive = !currentlyActive;

    matchingInputs.forEach((input) => {
      if (input instanceof HTMLInputElement) {
        input.checked = isActive;
      }
    });

    return isActive;
  }

  #isLashesActive() {
    const lashesTab = this.querySelector('[data-look-tab][data-is-lashes="true"]');
    if (lashesTab instanceof HTMLAnchorElement && lashesTab.classList.contains('look-tabs__link--active')) {
      return true;
    }

    const section = this.closest('.shopify-section');
    const facetsForm = this.#getFacetsForm(section);
    const lashesValue = (this.dataset.lashesValue ?? 'lashes').toLowerCase();

    if (facetsForm) {
      const groupInputs = facetsForm.querySelectorAll('input[type="checkbox"][name*="filter_group"]');
      const normalize = (s) => String(s ?? '').toLowerCase().replace(/[^a-z0-9]+/g, '-');
      const checkboxActive = Array.from(groupInputs).some(
        (input) =>
          input instanceof HTMLInputElement &&
          input.checked &&
          (input.value.toLowerCase() === lashesValue || normalize(input.value) === normalize(lashesValue))
      );

      if (checkboxActive) return true;
    }

    const groupParam = this.dataset.groupFilterParam;
    if (groupParam) {
      const url = new URL(window.location.href);
      return url.searchParams
        .getAll(groupParam)
        .some((value) => value.toLowerCase() === lashesValue);
    }

    return false;
  }

  #hasActiveStyleFilters(facetsForm) {
    if (!facetsForm) return false;

    const styleParam = this.#getStyleFilterParam();
    const styleInputs = facetsForm.querySelectorAll(
      `input[type="checkbox"][name="${CSS.escape(styleParam)}"]:checked`
    );

    return styleInputs.length > 0;
  }

  #getStyleFilterParam() {
    return this.dataset.styleFilterParam ?? 'filter.p.option.style';
  }

  #clearStyleFilters(facetsForm) {
    if (!facetsForm) return false;

    const styleParam = this.#getStyleFilterParam();
    const styleInputs = facetsForm.querySelectorAll(
      `input[type="checkbox"][name="${CSS.escape(styleParam)}"]:checked`
    );

    if (!styleInputs.length) return false;

    styleInputs.forEach((input) => {
      if (input instanceof HTMLInputElement) {
        input.checked = false;
      }
    });

    this.querySelectorAll('[data-filter-tier="secondary"][data-look-tab]').forEach((tab) => {
      if (tab instanceof HTMLAnchorElement) {
        this.#setTabPressed(tab, false);
      }
    });

    return true;
  }

  #getFacetsForm(section) {
    const ownForm = this.closest('facets-form-component');
    if (ownForm) return ownForm;

    if (!section) return null;

    const forms = section.querySelectorAll('facets-form-component');
    for (const form of forms) {
      if (form.querySelector('input[name^="filter."]')) {
        return form;
      }
    }

    return forms[0] ?? null;
  }
}

if (!customElements.get('look-tabs-component')) {
  customElements.define('look-tabs-component', LookTabsComponent);
}